import { useState } from 'react';
import { api } from '../lib/api.js';
import { useBlogs, useQuery, useWidgets } from '../lib/queries.js';
import { Button, Card, ErrorBox, Loading, PageHeader, Select, Stat } from '../components/ui/index.js';
import { num, pct } from '../lib/format.js';
import { useAuth } from '../lib/auth.js';

interface Summary {
  range: { from: string; to: string };
  totals: { impressions: number; clicks: number; views: number; ctr: number };
  series: { date: string; impressions: number; clicks: number; views: number; uniqueVisitors: number }[];
  topPosts: { id: string; title: string; views: number; clicks: number; url: string }[];
  topReferrers: { domain: string; count: number }[];
}

const RANGES = [
  { days: 7, label: '7 วันล่าสุด' },
  { days: 30, label: '30 วันล่าสุด' },
  { days: 90, label: '90 วันล่าสุด' },
];

export function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [blogId, setBlogId] = useState('');
  const [widgetId, setWidgetId] = useState('');
  const blogs = useBlogs();
  const widgets = useWidgets();
  const tenantId = useAuth((s) => s.activeTenantId);

  const from = new Date(Date.now() - (days - 1) * 86400_000).toISOString();

  const summary = useQuery({
    queryKey: ['analytics', days, blogId, widgetId],
    queryFn: () => {
      const p = new URLSearchParams({ from });
      if (blogId) p.set('blogId', blogId);
      if (widgetId) p.set('widgetId', widgetId);
      return api.get<{ data: Summary }>(`/v1/analytics/summary?${p}`).then((r) => r.data);
    },
  });

  async function exportCsv() {
    const p = new URLSearchParams({ from });
    const res = await api.raw(`/v1/analytics/export?${p}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `blogaris-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const s = summary.data;
  const max = Math.max(1, ...(s?.series.map((d) => Math.max(d.impressions, d.views)) ?? [1]));

  return (
    <>
      <PageHeader
        title="สถิติ"
        description="ภาพรวมการเข้าถึงบทความและประสิทธิภาพของ widget"
        actions={<Button variant="outline" onClick={exportCsv} disabled={!tenantId}>ดาวน์โหลด CSV</Button>}
      />

      <div className="mb-5 flex flex-wrap gap-3">
        <Select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-auto">
          {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
        </Select>
        <Select value={blogId} onChange={(e) => setBlogId(e.target.value)} className="w-auto">
          <option value="">ทุกบล็อก</option>
          {blogs.data?.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
        </Select>
        <Select value={widgetId} onChange={(e) => setWidgetId(e.target.value)} className="w-auto">
          <option value="">ทุก widget</option>
          {widgets.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </Select>
      </div>

      {summary.isLoading ? <Loading /> : summary.error ? (
        <ErrorBox error={summary.error} onRetry={() => void summary.refetch()} />
      ) : s ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="ยอดอ่านบทความ" value={num(s.totals.views)} />
            <Stat label="Impression" value={num(s.totals.impressions)} />
            <Stat label="คลิกจาก widget" value={num(s.totals.clicks)} />
            <Stat label="CTR" value={pct(s.totals.ctr)} sub="คลิก ÷ impression" />
          </div>

          <Card className="p-5">
            <h2 className="mb-4 text-sm font-semibold text-[var(--ink)]">แนวโน้มรายวัน</h2>
            <svg viewBox="0 0 100 36" className="h-40 w-full" preserveAspectRatio="none" role="img"
                 aria-label="กราฟแนวโน้มรายวัน">
              {s.series.map((d, i) => {
                const bw = 100 / s.series.length;
                return (
                  <g key={d.date}>
                    <rect x={i * bw + 0.25} y={36 - (d.impressions / max) * 36}
                          width={bw - 0.5} height={(d.impressions / max) * 36}
                          className="fill-brand-500/25" rx="0.4" />
                    <rect x={i * bw + 0.25} y={36 - (d.views / max) * 36}
                          width={bw - 0.5} height={(d.views / max) * 36}
                          className="fill-brand-500" rx="0.4" />
                    <title>{`${d.date} — impression ${d.impressions} · อ่าน ${d.views} · คลิก ${d.clicks} · ผู้ชมไม่ซ้ำ ${d.uniqueVisitors}`}</title>
                  </g>
                );
              })}
            </svg>
            <div className="mt-2 flex justify-between text-[11px] text-[var(--ink-3)]">
              <span>{s.series[0]?.date}</span>
              <span className="flex gap-3">
                <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-[var(--brand)]/25" />impression</span>
                <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm bg-[var(--brand)]" />ยอดอ่าน</span>
              </span>
              <span>{s.series[s.series.length - 1]?.date}</span>
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h2 className="mb-3 text-sm font-semibold text-[var(--ink)]">บทความยอดนิยม</h2>
              {s.topPosts.length ? (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-[var(--ink-3)]">
                    <tr><th className="pb-2 font-medium">บทความ</th><th className="pb-2 text-right font-medium">อ่าน</th><th className="pb-2 text-right font-medium">คลิก</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--line-soft)]">
                    {s.topPosts.map((p) => (
                      <tr key={p.id}>
                        <td className="py-2 pr-3">
                          <a href={p.url} target="_blank" rel="noopener" className="line-clamp-1 hover:text-[var(--brand)]">{p.title}</a>
                        </td>
                        <td className="py-2 text-right tabular-nums">{num(p.views)}</td>
                        <td className="py-2 text-right tabular-nums">{num(p.clicks)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-sm text-[var(--ink-3)]">ยังไม่มีข้อมูล</p>}
            </Card>

            <Card className="p-5">
              <h2 className="mb-3 text-sm font-semibold text-[var(--ink)]">เว็บที่ฝัง widget</h2>
              {s.topReferrers.length ? (
                <ul className="space-y-2">
                  {s.topReferrers.map((r) => (
                    <li key={r.domain} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate">{r.domain}</span>
                      <span className="tabular-nums text-[var(--ink-3)]">{num(r.count)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[var(--ink-3)]">
                  ยังไม่มีข้อมูล — ตัวเลขจะขึ้นเมื่อมีคนเข้าชมหน้าเว็บที่ฝัง widget ของคุณ
                </p>
              )}
            </Card>
          </div>
        </div>
      ) : null}
    </>
  );
}
